// Copyright (c) Ping. All rights reserved.
//
// [#2] Transport client for peer history-transfer (linking history hand-off).
// An existing device E ships a newly-linked device N a sealed bundle of
// decrypted AppEvents so N gets pre-link history; the bundle is HPKE-sealed +
// signed by the SDK (`MessagingClient.sealHistoryBundle`) so the server stays
// blind. Backend: `services/api/internal/recovery`.
//
// Endpoints:
//   POST /v1/history-transfers/{recipient_device_id}  { sealed_blob }  -> { transfer_id }
//   GET  /v1/history-transfers/inbox                                   -> { transfers: [...] }
//
// NOTE: the inbox GET is CONSUMING (the server's ConsumeInbox does a
// GETDEL-style RETURNING), so there is no separate ack/delete call — one GET
// drains and returns the pending transfers exactly once.

import { bytesToUrlB64NoPad, urlB64NoPadToBytes } from '@ping/ui';

import { api } from '@core/network/api';

interface PostTransferResponse {
  transfer_id: string;
}

interface InboxTransferWire {
  transfer_id: string;
  sender_device_id: string; // base64url device id
  sealed_blob: string; // base64url HPKE-sealed bundle
  created_at: string;
  // Total number of conversation bundles in this linking batch. The sender sets
  // it to the count of non-empty bundles it posts, so the receiver can show a
  // true-% sync banner. Absent for older senders (fall back to indeterminate).
  batch_total?: number | null;
}

interface InboxResponse {
  transfers: InboxTransferWire[];
}

export interface PendingHistoryTransfer {
  transferId: string;
  senderDeviceId: Uint8Array;
  sealedBlob: Uint8Array;
  /** Total bundles in this linking batch (the % banner's denominator), or
   *  undefined when the sender didn't supply it (older sender). */
  batchTotal?: number;
}

/**
 * Advertise THIS device's history-delivery X25519 pubkey so peers who share a
 * conversation can seal recovered history to it. Device-JWT scoped. Backend:
 * `POST /v1/devices/self/delivery-key { history_delivery_pubkey }`. Idempotent
 * server-side — re-advertising a fresh key just overwrites the prior one.
 */
export async function postSelfDeliveryKey(pubkeyB64Url: string): Promise<void> {
  await api.post('/v1/devices/self/delivery-key', {
    history_delivery_pubkey: pubkeyB64Url,
  });
}

/**
 * Ship a sealed history bundle to `recipientDeviceId` (base64url, 32 bytes).
 * `batchTotal` (optional) is the count of bundles in this linking batch — the
 * receiver uses it as the denominator for its true-% sync banner.
 */
export async function postHistoryTransfer(
  recipientDeviceIdB64Url: string,
  sealedBlob: Uint8Array,
  batchTotal?: number,
): Promise<string> {
  const resp = await api.post<PostTransferResponse>(
    `/v1/history-transfers/${encodeURIComponent(recipientDeviceIdB64Url)}`,
    {
      sealed_blob: bytesToUrlB64NoPad(sealedBlob),
      ...(batchTotal !== undefined ? { batch_total: batchTotal } : {}),
    },
  );
  return resp.transfer_id;
}

/**
 * Drain this device's pending history transfers (single consuming GET).
 * Returns decoded `{ senderDeviceId, sealedBlob }` ready for
 * `MessagingClient.openHistoryBundle`.
 */
export async function consumeHistoryTransferInbox(): Promise<PendingHistoryTransfer[]> {
  const resp = await api.get<InboxResponse>('/v1/history-transfers/inbox');
  return (resp.transfers ?? []).map((t) => ({
    transferId: t.transfer_id,
    senderDeviceId: urlB64NoPadToBytes(t.sender_device_id),
    sealedBlob: urlB64NoPadToBytes(t.sealed_blob),
    ...(typeof t.batch_total === 'number' ? { batchTotal: t.batch_total } : {}),
  }));
}
